/**
 * Validation rule catalogue — the *definition* of what rules exist per field
 * type. The runtime application of these rules lives in `validation-engine.ts`.
 */
import type { FieldType } from './definition-types'
import type { RuleSpec } from './types'

export type { RuleSpec }

export const RULE_CATALOG: Partial<Record<FieldType, RuleSpec[]>> = {
  text: [
    { type: 'min_length',  label: 'حداقل طول متن',  defaultSeverity: 'blocking' },
    { type: 'max_length',  label: 'حداکثر طول متن', defaultSeverity: 'blocking' },
    { type: 'regex_match', label: 'الگوی regex',    defaultSeverity: 'blocking' },
  ],
  textarea: [
    { type: 'min_length',  label: 'حداقل طول متن',  defaultSeverity: 'blocking' },
    { type: 'max_length',  label: 'حداکثر طول متن', defaultSeverity: 'blocking' },
    { type: 'regex_match', label: 'الگوی regex',    defaultSeverity: 'blocking' },
  ],
  number: [
    { type: 'numeric_range',         label: 'محدوده عددی',           defaultSeverity: 'blocking' },
    { type: 'physiological_range',   label: 'محدوده فیزیولوژیک',     defaultSeverity: 'soft_warning' },
    { type: 'max_decimal_places',    label: 'حداکثر اعشار',           defaultSeverity: 'blocking' },
    { type: 'integer_only',          label: 'فقط عدد صحیح',           defaultSeverity: 'blocking', noConfig: true },
    { type: 'value_greater_than_field', label: 'بزرگ‌تر از فیلد دیگر', defaultSeverity: 'blocking' },
    { type: 'value_less_than_field',    label: 'کوچک‌تر از فیلد دیگر', defaultSeverity: 'blocking' },
  ],
  date: [
    { type: 'date_in_past',         label: 'باید در گذشته باشد',     defaultSeverity: 'blocking', noConfig: true },
    { type: 'date_in_future',       label: 'باید در آینده باشد',     defaultSeverity: 'blocking', noConfig: true },
    { type: 'date_after_absolute',  label: 'بعد از تاریخ ثابت',      defaultSeverity: 'blocking' },
    { type: 'date_before_absolute', label: 'قبل از تاریخ ثابت',      defaultSeverity: 'blocking' },
    { type: 'date_after_relative',  label: 'بعد از تاریخ نسبی',      defaultSeverity: 'blocking' },
    { type: 'date_before_relative', label: 'قبل از تاریخ نسبی',      defaultSeverity: 'blocking' },
    { type: 'date_after_field',     label: 'بعد از فیلد دیگر',       defaultSeverity: 'blocking' },
    { type: 'date_before_field',    label: 'قبل از فیلد دیگر',       defaultSeverity: 'blocking' },
  ],
  datetime: [
    { type: 'date_in_past',         label: 'باید در گذشته باشد',     defaultSeverity: 'blocking', noConfig: true },
    { type: 'date_in_future',       label: 'باید در آینده باشد',     defaultSeverity: 'blocking', noConfig: true },
    { type: 'date_after_absolute',  label: 'بعد از تاریخ ثابت',      defaultSeverity: 'blocking' },
    { type: 'date_before_absolute', label: 'قبل از تاریخ ثابت',      defaultSeverity: 'blocking' },
    { type: 'date_after_relative',  label: 'بعد از تاریخ نسبی',      defaultSeverity: 'blocking' },
    { type: 'date_before_relative', label: 'قبل از تاریخ نسبی',      defaultSeverity: 'blocking' },
    { type: 'date_after_field',     label: 'بعد از فیلد دیگر',       defaultSeverity: 'blocking' },
    { type: 'date_before_field',    label: 'قبل از فیلد دیگر',       defaultSeverity: 'blocking' },
  ],
  time: [
    { type: 'time_after',  label: 'بعد از ساعت',  defaultSeverity: 'blocking' },
    { type: 'time_before', label: 'قبل از ساعت',  defaultSeverity: 'blocking' },
  ],
  multi_select: [
    { type: 'min_selected', label: 'حداقل تعداد انتخاب', defaultSeverity: 'blocking' },
    { type: 'max_selected', label: 'حداکثر تعداد انتخاب', defaultSeverity: 'blocking' },
  ],
  file: [
    { type: 'file_extension',  label: 'پسوندهای مجاز',          defaultSeverity: 'blocking' },
    { type: 'max_file_size',   label: 'حداکثر حجم فایل (MB)',   defaultSeverity: 'blocking' },
  ],
  image: [
    { type: 'file_extension',     label: 'پسوندهای مجاز',          defaultSeverity: 'blocking' },
    { type: 'max_file_size',      label: 'حداکثر حجم فایل (MB)',   defaultSeverity: 'blocking' },
    { type: 'max_image_width',    label: 'حداکثر عرض تصویر (px)',  defaultSeverity: 'blocking' },
    { type: 'max_image_height',   label: 'حداکثر ارتفاع تصویر (px)', defaultSeverity: 'blocking' },
  ],
  repeatable_array: [
    { type: 'min_entries', label: 'حداقل تعداد ردیف',  defaultSeverity: 'blocking' },
    { type: 'max_entries', label: 'حداکثر تعداد ردیف', defaultSeverity: 'blocking' },
  ],
}

export function availableRulesFor(fieldType: FieldType): RuleSpec[] {
  return RULE_CATALOG[fieldType] ?? []
}

export function ruleSpec(fieldType: FieldType, ruleType: string): RuleSpec | undefined {
  return RULE_CATALOG[fieldType]?.find((r) => r.type === ruleType)
}
